import { TWarboxMessageActionTypes, TWarboxMessageInitialState } from './types';
import { updateWarboxMessageState } from './utils';
import {
    DEFAULT_WARBOX_MESSAGE_STATE,
    ADD_WARBOX_MESSAGE,
    GET_WARBOX_MESSAGES,
    GET_WARBOX_MESSAGES_BY_CONVERSATION,
    GET_WARBOX_GALLERY_MESSAGES_BY_CONVERSATION,
    UPSERT_WARBOX_MESSAGE,
    SET_GALLERY_LOADING,
    DELETE_WARBOX_MESSAGE,
    CLEAR_WARBOX_CONVERSATION_MESSAGES,
} from './constants';
import { CLEAR_STATE, SET_ERROR, SET_LOADING } from '../types';

const getMessageClientId = (message: any) => message?.metadata?.clientMessageId;

const getPopulatedSentBy = (message: any, incomingMessage: any) => {
    const incomingSentBy = incomingMessage?.sentBy;

    if (incomingSentBy?.name) return incomingSentBy;
    if (message?.sentBy?.name) return message.sentBy;

    return incomingSentBy;
};

const getMessageText = (message: any) =>
    message?.content?.text || message?.message || message?.Body || '';

const getMessageConversationId = (message: any) => {
    const conversation = message?.conversation;
    if (typeof conversation === 'string') return conversation;

    return conversation?._id || conversation?.id || '';
};

const getMessageRecipientPhone = (message: any) =>
    message?.recipient?.normalizedPhone ||
    message?.recipient?.phone ||
    message?.To ||
    '';

const isSameOptimisticMessage = (message: any, incomingMessage: any) =>
    message?.status === 'queued' &&
    message?.metadata?.optimistic &&
    message?.direction === incomingMessage?.direction &&
    getMessageConversationId(message) ===
        getMessageConversationId(incomingMessage) &&
    getMessageRecipientPhone(message) ===
        getMessageRecipientPhone(incomingMessage) &&
    getMessageText(message) === getMessageText(incomingMessage);

const upsertMessage = (messages: any[] = [], incomingMessage: any) => {
    const incomingClientId = getMessageClientId(incomingMessage);
    const incomingId = incomingMessage?._id;
    const messageIndex = messages.findIndex((message) => {
        const currentClientId = getMessageClientId(message);

        return (
            (incomingId && message?._id === incomingId) ||
            (incomingClientId && currentClientId === incomingClientId) ||
            isSameOptimisticMessage(message, incomingMessage)
        );
    });

    if (messageIndex === -1) return [...messages, incomingMessage];

    return messages.map((message, index) => {
        if (index !== messageIndex) return message;

        return {
            ...incomingMessage,
            sentBy: getPopulatedSentBy(message, incomingMessage),
            metadata: {
                ...(message.metadata || {}),
                ...(incomingMessage.metadata || {}),
                optimistic: false,
            },
        };
    });
};

const getMessageId = (message: any) =>
    message?._id ||
    message?.id ||
    message?.wspId ||
    getMessageClientId(message);

const getMessageTime = (message: any) =>
    new Date(message?.createdAt || message?.created_at || 0).getTime();

const sortMessagesByCreatedAt = (messages: any[] = []) =>
    [...messages].sort((a, b) => getMessageTime(a) - getMessageTime(b));

const mergeMessages = (
    currentMessages: any[] = [],
    incomingMessages: any[] = []
) => {
    const messagesById = new Map<string, any>();
    const messagesWithoutId: any[] = [];

    [...currentMessages, ...incomingMessages].forEach((message) => {
        const messageId = getMessageId(message);

        if (!messageId) {
            messagesWithoutId.push(message);
            return;
        }

        messagesById.set(messageId, {
            ...(messagesById.get(messageId) || {}),
            ...message,
        });
    });

    return sortMessagesByCreatedAt([
        ...Array.from(messagesById.values()),
        ...messagesWithoutId,
    ]);
};

export default (
    state: TWarboxMessageInitialState,
    action: TWarboxMessageActionTypes
): TWarboxMessageInitialState => {
    switch (action.type) {
        case CLEAR_STATE:
            return updateWarboxMessageState(
                state,
                DEFAULT_WARBOX_MESSAGE_STATE
            );
        case GET_WARBOX_MESSAGES_BY_CONVERSATION: {
            const mergeStrategy = action.payload.mergeStrategy || 'replace';
            const messages =
                mergeStrategy === 'replace'
                    ? sortMessagesByCreatedAt(action.payload.messages)
                    : mergeMessages(
                          state.messages || [],
                          action.payload.messages
                      );

            return updateWarboxMessageState(state, {
                messages,
                total:
                    mergeStrategy === 'replace'
                        ? action.payload.total
                        : Math.max(action.payload.total, state.total || 0),
                loading: false,
                error: null,
            });
        }
        case GET_WARBOX_GALLERY_MESSAGES_BY_CONVERSATION:
            return updateWarboxMessageState(state, {
                galleryMessages: action.payload.messages,
                galleryTotal: action.payload.total,
                galleryLoading: false,
                error: null,
            });
        case SET_GALLERY_LOADING:
            return updateWarboxMessageState(state, {
                galleryLoading: action.payload,
            });
        case GET_WARBOX_MESSAGES:
            return updateWarboxMessageState(state, {
                messages: action.payload.messages,
                total: action.payload.total,
                loading: false,
                error: null,
            });
        case ADD_WARBOX_MESSAGE:
            return updateWarboxMessageState(state, {
                messages: [...(state.messages || []), action.payload],
                total: (state.total || 0) + 1,
                loading: false,
                error: null,
            });
        case UPSERT_WARBOX_MESSAGE:
            return updateWarboxMessageState(state, {
                messages: upsertMessage(state.messages || [], action.payload),
                loading: false,
                error: null,
            });
        case DELETE_WARBOX_MESSAGE: {
            const messageId = action.payload.messageId;

            return updateWarboxMessageState(state, {
                messages: (state.messages || []).filter((message) => {
                    const currentId =
                        message?._id ||
                        message?.id ||
                        message?.wspId ||
                        message?.metadata?.clientMessageId;

                    return currentId !== messageId;
                }),
                total: Math.max((state.total || 0) - 1, 0),
                loading: false,
                error: null,
            });
        }
        case CLEAR_WARBOX_CONVERSATION_MESSAGES:
            return updateWarboxMessageState(state, {
                messages: [],
                total: 0,
                galleryMessages: [],
                galleryTotal: 0,
                loading: false,
                galleryLoading: false,
                error: null,
            });
        case SET_ERROR:
            return {
                ...state,
                error: action.payload,
                loading: false,
                galleryLoading: false,
            };
        case SET_LOADING:
            return updateWarboxMessageState(state, {
                loading: true,
            });
        default:
            return state;
    }
};
